import { useState, useEffect, useCallback, useRef } from 'react';
import { reportApi } from '../../api/report.api';
import { useUI } from '../../store/UIContext';
import { PageHeader, Btn, Spinner, Badge, EmptyState } from '../../components/ui/index';
import { EmojiBar } from '../../components/ui/EmojiPicker';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import FinanceInvoicePrintForm from '../../components/reports/FinanceInvoicePrintForm';
import AminInvoicePicker from '../../components/reports/AminInvoicePicker';
import { toPersian, formatDate } from '../../utils/persianNumber';
import { Receipt } from '@phosphor-icons/react';

const STATUS_VARIANT = {
  pending_price: 'amber',
  priced: 'blue',
  approved: 'green',
  finalized: 'gray',
};

const STATUS_TABS = [
  { id: '', label: 'همه' },
  { id: 'pending_price', label: 'قیمت‌گذاری نشده' },
  { id: 'priced', label: 'قیمت‌گذاری شده' },
  { id: 'approved', label: 'تایید شده' },
  { id: 'finalized', label: 'نهایی' },
];

const emptyPriceForm = () => ({
  amount: '',
  finance_note: '',
  invoice_number: '',
  receiver_manager_name: '',
  receiver_responsible_name: '',
  format_hint: '',
  product_code: '',
  product_name: '',
  amin_customer_code: '',
  amin_customer_name: '',
});

const EDITABLE_STATUSES = ['pending_price', 'priced', 'approved'];

function formatAmount(amount) {
  if (amount == null) return '—';
  return `${toPersian(Number(amount).toLocaleString('fa-IR'))} ریال`;
}
export default function InvoiceListPage() {
  const { addToast } = useUI();
  const [invoices, setInvoices] = useState([]);
  const [canEdit, setCanEdit] = useState(false);
  const [readOnly, setReadOnly] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);
  const [setupMessage, setSetupMessage] = useState('');
  const [priceModal, setPriceModal] = useState(null);
  const [priceForm, setPriceForm] = useState(emptyPriceForm());
  const [printInvoice, setPrintInvoice] = useState(null);
  const [bridgeConfigured, setBridgeConfigured] = useState(false);
  const financeNoteRef = useRef(null);

  const load = useCallback(async (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    try {
      const res = await reportApi.getDailyReportInvoices(
        statusFilter ? { status: statusFilter } : {},
      );
      setInvoices(Array.isArray(res.data?.invoices) ? res.data.invoices : []);
      setCanEdit(!!(res.data?.can_edit ?? res.data?.can_price));
      setReadOnly(!!res.data?.read_only);
      setSetupMessage(res.data?.setup_required ? (res.data.setup_message || 'پیکربندی دیتابیس ناقص است') : '');
    } catch (err) {
      if (showSpinner) {
        const msg = err.response?.data;
        addToast(typeof msg === 'string' && msg ? msg : 'خطا در بارگذاری لیست فاکتورها', 'error');
      }
    }
    setLoading(false);
  }, [addToast, statusFilter]);

  useEffect(() => { load(); }, [load]);
  useAutoRefresh(() => load(false), { paused: !!priceModal || !!printInvoice || !!acting });

  useEffect(() => {
    reportApi.getAccountingBridgeConfig()
      .then((res) => setBridgeConfigured(!!res.data?.configured))
      .catch(() => setBridgeConfigured(false));
  }, []);

  const openInvoiceModal = async (inv) => {
    const isNew = inv.status === 'pending_price';
    setPriceModal(inv);
    setPriceForm(emptyPriceForm());

    if (isNew) {
      try {
        const [numRes, defRes] = await Promise.all([
          reportApi.getNextInvoiceNumber(),
          reportApi.getCompanyInvoiceDefaults({ company_name: inv.company_name }),
        ]);
        setPriceForm({
          amount: inv.amount != null ? String(inv.amount) : '',
          finance_note: inv.finance_note || '',
          invoice_number: numRes.data?.invoice_number || '',
          receiver_manager_name: defRes.data?.receiver_manager_name || '',
          receiver_responsible_name: defRes.data?.receiver_responsible_name || '',
          format_hint: numRes.data?.format_hint || '',
          product_code: inv.product_code || '',
          product_name: inv.product_name || '',
          amin_customer_code: inv.amin_customer_code || '',
          amin_customer_name: inv.amin_customer_name || '',
        });
      } catch {
        setPriceForm({
          amount: inv.amount != null ? String(inv.amount) : '',
          finance_note: inv.finance_note || '',
          invoice_number: '',
          receiver_manager_name: '',
          receiver_responsible_name: '',
          format_hint: '',
          product_code: inv.product_code || '',
          product_name: inv.product_name || '',
          amin_customer_code: inv.amin_customer_code || '',
          amin_customer_name: inv.amin_customer_name || '',
        });
      }
      return;
    }

    let receiverManager = inv.receiver_manager_name || '';
    let receiverResponsible = inv.receiver_responsible_name || '';
    if (!receiverManager && !receiverResponsible) {
      try {
        const defRes = await reportApi.getCompanyInvoiceDefaults({ company_name: inv.company_name });
        receiverManager = defRes.data?.receiver_manager_name || '';
        receiverResponsible = defRes.data?.receiver_responsible_name || '';
      } catch { /* keep invoice values */ }
    }

    setPriceForm({
      amount: inv.amount != null ? String(inv.amount) : '',
      finance_note: inv.finance_note || '',
      invoice_number: inv.invoice_number || '',
      receiver_manager_name: receiverManager,
      receiver_responsible_name: receiverResponsible,
      format_hint: '',
      product_code: inv.product_code || '',
      product_name: inv.product_name || '',
      amin_customer_code: inv.amin_customer_code || '',
      amin_customer_name: inv.amin_customer_name || '',
    });
  };

  const submitInvoiceForm = async () => {
    const amount = parseFloat(priceForm.amount);
    if (Number.isNaN(amount) || amount < 0) {
      addToast('مبلغ معتبر وارد کنید', 'error');
      return;
    }
    if (!priceForm.invoice_number.trim()) {
      addToast('شماره فاکتور الزامی است', 'error');
      return;
    }
    const isNew = priceModal.status === 'pending_price';
    const payload = {
      invoice_id: priceModal.invoice_id,
      amount,
      finance_note: priceForm.finance_note.trim(),
      invoice_number: priceForm.invoice_number.trim(),
      receiver_manager_name: priceForm.receiver_manager_name.trim(),
      receiver_responsible_name: priceForm.receiver_responsible_name.trim(),
      product_code: priceForm.product_code.trim(),
      product_name: priceForm.product_name.trim(),
      amin_customer_code: priceForm.amin_customer_code.trim(),
      amin_customer_name: priceForm.amin_customer_name.trim(),
    };
    setActing(priceModal.invoice_id);
    try {
      const res = isNew
        ? await reportApi.priceDailyReportInvoice(payload)
        : await reportApi.updateDailyReportInvoice(payload);
      addToast(
        isNew
          ? `فاکتور ${res.data?.invoice_number || ''} ثبت شد`
          : 'فاکتور به‌روزرسانی شد',
        'success',
      );
      setPriceModal(null);
      await load();
    } catch (err) {
      const msg = err.response?.data;
      addToast(typeof msg === 'string' && msg ? msg : (isNew ? 'خطا در ثبت قیمت' : 'خطا در ویرایش فاکتور'), 'error');
    }
    setActing(null);
  };

  const runAction = async (fn, invoiceId, successMsg) => {
    setActing(invoiceId);
    try {
      await fn({ invoice_id: invoiceId });
      addToast(successMsg, 'success');
      await load();
    } catch (err) {
      const msg = err.response?.data;
      addToast(typeof msg === 'string' && msg ? msg : 'عملیات ناموفق بود', 'error');
    }
    setActing(null);
  };

  const openPrint = async (inv) => {
    try {
      const res = await reportApi.getDailyReportInvoiceDetail({ invoice_id: inv.invoice_id });
      setPrintInvoice(res.data);
      setTimeout(() => window.print(), 300);
    } catch {
      addToast('خطا در بارگذاری فاکتور برای چاپ', 'error');
    }
  };

  const showActions = canEdit || readOnly;
  const isNewInvoiceModal = priceModal?.status === 'pending_price';

  const inputStyle = {
    width: '100%', padding: '10px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8,
    fontSize: 13, marginBottom: 12, boxSizing: 'border-box', fontFamily: 'inherit',
  };

  return (
    <div className="fade-in">
      <FinanceInvoicePrintForm invoice={printInvoice} />

      <PageHeader
        title="لیست فاکتورها"
        subtitle={
          readOnly
            ? 'فقط مشاهده — صدور و قیمت‌گذاری با مدیر مالی'
            : canEdit
              ? 'مدیر مالی: شماره استاندارد، قیمت، چاپ فرم رسمی'
              : 'فاکتورهای ثبت‌شده از گزارش روزانه'
        }
      />

      {readOnly && (
        <div style={{
          background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10,
          padding: '10px 14px', marginBottom: 16, fontSize: 12, color: '#64748b',
        }}>
          شما به‌عنوان دیسپچر فقط لیست را می‌بینید. شماره‌گذاری و چاپ رسمی با <strong>مدیر مالی</strong> است.
        </div>
      )}

      {setupMessage && (
        <div style={{
          background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10,
          padding: '12px 16px', marginBottom: 16, color: '#b91c1c', fontSize: 13, lineHeight: 1.8,
        }}>
          {setupMessage}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.id || 'all'}
            type="button"
            onClick={() => setStatusFilter(tab.id)}
            style={{
              padding: '6px 14px', borderRadius: 16, cursor: 'pointer', fontFamily: 'inherit',
              fontSize: 12, fontWeight: 600,
              border: `1.5px solid ${statusFilter === tab.id ? '#29AAE1' : '#e2e8f0'}`,
              background: statusFilter === tab.id ? '#EDF3FC' : '#fff',
              color: statusFilter === tab.id ? '#233A7A' : '#64748b',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}><Spinner /></div>
      ) : invoices.length === 0 ? (
        <EmptyState icon={<Receipt size={44} weight="regular" />} title="فاکتوری یافت نشد" />
      ) : (
        <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                  {['شماره فاکتور', 'شرکت', 'عنوان', 'کد کالا', 'کارمند', 'تاریخ', 'مبلغ', 'وضعیت', showActions ? 'عملیات' : null]
                    .filter(Boolean)
                    .map((h) => (
                      <th key={h} style={{ padding: '12px 14px', textAlign: 'right', fontWeight: 700, color: '#475569', whiteSpace: 'nowrap' }}>
                        {h}
                      </th>
                    ))}
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.invoice_id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '12px 14px', fontWeight: 700, color: '#1B3068', whiteSpace: 'nowrap' }}>
                      {inv.invoice_number ? toPersian(inv.invoice_number) : `#${toPersian(inv.invoice_id)}`}
                    </td>
                    <td style={{ padding: '12px 14px', fontWeight: 600, color: '#0f172a' }}>{inv.company_display || inv.company_name}</td>
                    <td style={{ padding: '12px 14px', color: '#334155', maxWidth: 200 }}>
                      <div style={{ fontWeight: 600 }}>{inv.item_title}</div>
                    </td>
                    <td style={{ padding: '12px 14px', color: '#64748b', whiteSpace: 'nowrap', fontSize: 11 }}>
                      {inv.product_code ? toPersian(inv.product_code) : '—'}
                    </td>
                    <td style={{ padding: '12px 14px', color: '#64748b', whiteSpace: 'nowrap' }}>
                      {(inv.employee_name || '').trim() || '—'}
                    </td>
                    <td style={{ padding: '12px 14px', color: '#64748b', whiteSpace: 'nowrap' }}>{formatDate(inv.report_date)}</td>
                    <td style={{ padding: '12px 14px', fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap' }}>{formatAmount(inv.amount)}</td>
                    <td style={{ padding: '12px 14px' }}>
                      <Badge variant={STATUS_VARIANT[inv.status] || 'gray'}>{inv.status_label}</Badge>
                    </td>
                    {showActions && (
                      <td style={{ padding: '12px 14px', whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {canEdit && EDITABLE_STATUSES.includes(inv.status) && (
                            <Btn size="sm" onClick={() => openInvoiceModal(inv)} disabled={acting === inv.invoice_id}>
                              {inv.status === 'pending_price' ? 'صدور / قیمت' : 'ویرایش'}
                            </Btn>
                          )}
                          {canEdit && inv.status === 'priced' && (
                            <Btn size="sm" variant="secondary" onClick={() => runAction(reportApi.approveDailyReportInvoice, inv.invoice_id, 'تایید شد')} disabled={acting === inv.invoice_id}>تایید</Btn>
                          )}
                          {canEdit && inv.status === 'approved' && (
                            <Btn size="sm" onClick={() => runAction(reportApi.finalizeDailyReportInvoice, inv.invoice_id, 'نهایی شد')} disabled={acting === inv.invoice_id}>نهایی</Btn>
                          )}
                          {inv.invoice_number && (
                            <Btn size="sm" variant="secondary" onClick={() => openPrint(inv)}>🖨️ چاپ</Btn>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {priceModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 16,
        }}
        >
          <div style={{
            background: '#fff', borderRadius: 14, padding: '24px 28px', width: '100%',
            maxWidth: 'min(900px, 95vw)',
            maxHeight: '90vh', overflowY: 'auto',
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
            flexShrink: 0,
          }}
          >
            <h3 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 4px', color: '#0f172a' }}>
              {isNewInvoiceModal ? 'صدور فاکتور رسمی' : 'ویرایش فاکتور'}
            </h3>
            <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 16px' }}>
              {priceModal.company_display || priceModal.company_name} — {priceModal.item_title}
              {!isNewInvoiceModal && priceModal.status_label ? ` · ${priceModal.status_label}` : ''}
            </p>

            <label style={{ fontSize: 12, fontWeight: 600, color: '#334155', display: 'block', marginBottom: 6 }}>
              شماره فاکتور (استاندارد) *
            </label>
            <input
              value={priceForm.invoice_number}
              onChange={(e) => setPriceForm((p) => ({ ...p, invoice_number: e.target.value }))}
              placeholder="PR-1404-00001"
              style={inputStyle}
            />
            {priceForm.format_hint && (
              <p style={{ fontSize: 10, color: '#94a3b8', margin: '-8px 0 12px' }}>{priceForm.format_hint}</p>
            )}

            <AminInvoicePicker
              bridgeConfigured={bridgeConfigured}
              value={{
                product_code: priceForm.product_code,
                product_name: priceForm.product_name,
                amin_customer_code: priceForm.amin_customer_code,
                amin_customer_name: priceForm.amin_customer_name,
                amount: priceForm.amount,
              }}
              onChange={(patch) => setPriceForm((p) => ({ ...p, ...patch }))}
            />

            <label style={{ fontSize: 12, fontWeight: 600, color: '#334155', display: 'block', marginBottom: 6 }}>مبلغ (ریال) *</label>
            <input type="number" min="0" value={priceForm.amount} onChange={(e) => setPriceForm((p) => ({ ...p, amount: e.target.value }))} style={inputStyle} />

            <label style={{ fontSize: 12, fontWeight: 600, color: '#334155', display: 'block', marginBottom: 6 }}>مدیر شرکت گیرنده</label>
            <input value={priceForm.receiver_manager_name} onChange={(e) => setPriceForm((p) => ({ ...p, receiver_manager_name: e.target.value }))} style={inputStyle} />

            <label style={{ fontSize: 12, fontWeight: 600, color: '#334155', display: 'block', marginBottom: 6 }}>مسئول / نماینده شرکت گیرنده</label>
            <input value={priceForm.receiver_responsible_name} onChange={(e) => setPriceForm((p) => ({ ...p, receiver_responsible_name: e.target.value }))} style={inputStyle} />

            <label style={{ fontSize: 12, fontWeight: 600, color: '#334155', display: 'block', marginBottom: 6 }}>توضیحات مالی</label>
            <textarea ref={financeNoteRef} value={priceForm.finance_note} onChange={(e) => setPriceForm((p) => ({ ...p, finance_note: e.target.value }))} rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
            <div style={{ marginBottom: 12 }}>
              <EmojiBar
                inputRef={financeNoteRef}
                value={priceForm.finance_note}
                onInsert={(next) => setPriceForm((p) => ({ ...p, finance_note: next }))}
              />
            </div>

            <p style={{ fontSize: 11, color: '#64748b', lineHeight: 1.7, marginBottom: 16 }}>
              سربرگ: <strong>شرکت پژواک رایانه نقش جهان</strong> — امضای مدیر مالی پس از ثبت در فرم چاپ درج می‌شود.
            </p>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Btn variant="secondary" onClick={() => setPriceModal(null)} disabled={acting}>انصراف</Btn>
              <Btn onClick={submitInvoiceForm} loading={acting === priceModal.invoice_id}>
                {isNewInvoiceModal ? 'ثبت فاکتور' : 'ذخیره تغییرات'}
              </Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
